/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, Fragment, useMemo, useCallback, useState, useEffect } from "react";

// antd:
// import { LoadingOutlined } from "@ant-design/icons";
import { Form, Segmented } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
import AppIcons from "../react/AppIcons.tsx";
import SpanE from "../react/SpanE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getLng, getDict, getTheme, getAppState, changeAppState, getUser, onChangeTheme, onChangeCompact, onChangeLng, onChangeShowDesktopIconsTitle, getSafePtr } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

// const { Option } = Select;
const { rz } = window;

interface Option {
  value:string;
  label?:React.ReactNode;
  disabled?:boolean;
}

type Tcomponent = {
  size:"large" | "middle" | "small",
  block?:boolean
};

const RZSegmented:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZSegmented.displayName = "Segmented";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const theme = useAppSelector(getTheme);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type, content, hasitems} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key));
  const elState = appState[fullPath];
  const {color, backgroundColor} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes.key || rz.strMakeID(24)]), [rootAttributes.key]);

  const isParentForm = useMemo(() => {
    const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
    return Boolean(parentState.name === "form");
  }, [appState, fullPath]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const cmpType = useMemo<Tcomponent>(() => {
    const typ:Tcomponent = {size: "middle"};
    (type || "").replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~["small", "middle", "large"].indexOf(tt)) { typ.size = tt }
      else if (~["block", "expand"].indexOf(tt)) { typ.block = true }
    });

    if (attributes?.size) { typ.size = attributes.size }
    if (attributes?.block) { typ.block = (attributes.block === "true") }
    return typ;
  }, [type, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    // if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    return est;
  }, [elState?.userStyle, elState?.visible, color, backgroundColor, theme, permissions]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title = useMemo<string | undefined>(() => (elState.attributes["title_" + lng] || elState.attributes.title || elState.attributes["title_" + rlng] || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng]), [elState.attributes, attributes, lng]);
  // eslint-disable-next-line max-len, react-hooks/exhaustive-deps
  const title2 = useMemo<string | undefined>(() => (elState?.attributes["title2_" + lng] || elState?.attributes.title2 || elState?.attributes["title2_" + rlng] || attributes["title2_" + lng] || attributes.title2 || attributes["title2_" + rlng]), [elState?.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);

  const contentWithIcon = useCallback(({itemValue, itemIcon, itemIconClass, itemIconColor, itemTooltip}:{itemValue:string, itemIcon?:string, itemIconClass?:string, itemIconColor?:string, itemTooltip?:string}) => {
    let ico:React.ReactElement | undefined;
    if (itemIcon) { ico = (<AppIcons icopath={itemIcon} attrs={{className: itemIconClass || "rz-margin-right-5", color: itemIconColor}} />) }
    return (
      <>
        {ico}
        <SpanE key={(itemValue || "").replace((/\s+/igm), "") + "-span"} uk-tooltip={itemTooltip} >
          {itemValue}
        </SpanE>
      </>
    );
  }, []);

  const items = useMemo<Array<Option>>(() => {
    let itms:Array<Option> = [];
    const sitms = elState?.items;
    if (sitms) {
      if (rz.isArray(sitms)) {
        itms = sitms.map((aa, a) => {
          const perm1 = fnc.checkPermissions(user, {...aa?.attributes, rootAttributes: {key: rootAttributes.key}});
          return {
            value: (rz.isString(aa) ? aa : (aa.value || aa.key || (key + "-" + a))),
            label: contentWithIcon({
              itemValue: (rz.isString(aa) ? aa : (aa["title_" + lng] || aa.title || aa["title_" + rlng] || aa["label_" + lng] || aa.label || aa["label_" + rlng] || "")),
              itemIcon: aa?.icon, itemIconClass: aa?.iconClass, itemIconColor: aa?.iconColor, itemTooltip: aa?.toltip
            }),
            disabled: (perm1.disabled === true)
          };
        });
      }
      else if (rz.isString(sitms)) { itms = (sitms.split("|")).map((value) => ({value})) }
    }
    else if (hasitems && content && rz.isArray(content)) {
      itms = content.map((aa, a) => {
        const perm1 = fnc.checkPermissions(user, {...aa?.attributes, rootAttributes: {key: rootAttributes.key}}), state1 = appState[aa?.fullPath];
        return {
          value: (rz.isString(aa) ? aa : (aa.value || aa.key || (key + "-" + a))),
          label: contentWithIcon({
            itemValue: (rz.isString(aa) ? aa : (aa.attributes["title_" + lng] || aa.attributes.title || aa.attributes["title_" + rlng] || aa.attributes["label_" + lng] || aa.attributes.label || aa.attributes["label_" + rlng] || "")),
            itemIcon: aa?.attributes?.icon, itemIconClass: aa?.attributes?.iconClass, itemIconColor: aa?.attributes?.iconColor, itemTooltip: aa?.attributes?.toltip
          }),
          disabled: (state1?.disabled || perm1.disabled === true)
        };
      });
    }
    return itms;
  }, [elState?.items, hasitems, content, user, rootAttributes.key, key, contentWithIcon, lng, rlng, appState]);

  const onChange = useCallback((value) => {
    if (fnc.config?.isDev) console.log({action: "<Segmented key='" + key + "'> -> onChange()", value});
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {...elState, value}}));
    if (rootNamespace && (attributes.onChange || attributes.onchange) && rootNamespace[attributes.onChange || attributes.onchange]) {
      const clientFNC = rootNamespace[attributes.onChange || attributes.onchange];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
          if (ret && ret?.globalState) {
            if (ret.globalState.lng) { dispatch(onChangeLng(ret.globalState.lng, true)) }
            if (!rz.isUndefinedOrNull(ret.globalState.theme)) { dispatch(onChangeTheme(ret.globalState.theme, true)) }
            if (!rz.isUndefinedOrNull(ret.globalState.compact)) { dispatch(onChangeCompact(ret.globalState.compact, true)) }
            if (!rz.isUndefinedOrNull(ret.globalState.showDesktopIconsTitle)) { dispatch(onChangeShowDesktopIconsTitle(ret.globalState.showDesktopIconsTitle, true)) }
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
        if (ret && ret?.globalState) {
          if (ret.globalState.lng) { dispatch(onChangeLng(ret.globalState.lng, true)) }
          if (!rz.isUndefinedOrNull(ret.globalState.theme)) { dispatch(onChangeTheme(ret.globalState.theme, true)) }
          if (!rz.isUndefinedOrNull(ret.globalState.compact)) { dispatch(onChangeCompact(ret.globalState.compact, true)) }
          if (!rz.isUndefinedOrNull(ret.globalState.showDesktopIconsTitle)) { dispatch(onChangeShowDesktopIconsTitle(ret.globalState.showDesktopIconsTitle, true)) }
        }
      }
    }
  }, [key, dispatch, rootAttributes.key, fullPath, elState, rootNamespace, attributes, user, lng, dct, safe]);

  const wrapperForm = useCallback((mainComponent:React.ReactNode) => {
    if (isParentForm) {
      const frmItemProps:{[key:string]:any} = {
        key: key + "-formitem", label: (<span className="rz-txt-std">{title2}</span>), className: "rz-width-expand",
        style: ((elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined)
      };
      // eslint-disable-next-line max-len
      return (
        <Form.Item {...frmItemProps}>
          {mainComponent}
        </Form.Item>
      )
    }
    return mainComponent;
  }, [isParentForm, title2, key, elState?.visible, permissions.visible]);

  return wrapperForm(
    <Segmented
      key={key} style={styl} className={clsu.join(" ")} uk-tooltip={tooltip}
      options={items}
      value={elState.value}
      // defaultValue={elState.value}
      placeholder={(attributes["placeholder_" + lng] || attributes.placeholder || attributes["placeholder_" + rlng] || "")}
      size={cmpType.size}
      block={cmpType.block}
      onChange={onChange}
      disabled={elState?.disabled || permissions.disabled === true}
      hidden={elState?.visible === false || permissions.visible === false}
    />
  );
}

export default RZSegmented;
/* eslint-enable @typescript-eslint/no-unused-vars */
